import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useCast } from '../../../chromecast/hooks/use-cast';
import { hasCastSession } from '../../../chromecast/utils/assert';
import { useRecordings } from '../../../queries/ats/recordings/hooks';
import { getSingleRecordingBy } from '../../../queries/ats/recordings/utils/get-recording-by';
import { useSearchParams } from '../../../routes/utils/use-search-params';
import { ErrorBoundary } from '../../error-boundary';
import { PlayerErrorFallback } from '../ats-player/player-error-fallback/player-error-fallback';
import { PlayerLoadingSpinner } from '../ats-player/player-loading-spinner';
import { ChromecastRecordingSenderLoader } from './chromecast-recording-sender-loader';
import RecordingClientPlayerLoader from './recording-client-player-loader';

interface RouteParams {
  id: string;
}

export default function RecordingPlayer() {
  const params = useParams<RouteParams>();
  const search = useSearchParams();

  const cast = useCast();

  const position = search.get('position');
  const positionAsNumber = position ? parseInt(position, 10) : undefined;

  const castMediaPosition = cast.media !== undefined ? cast.media.getEstimatedTime() : undefined;

  const { data: recordings, isLoading } = useRecordings();

  const recording = useMemo(
    () => (recordings ? getSingleRecordingBy(recordings, params.id, (rec) => rec.id) : undefined),
    [recordings, params.id],
  );

  if (isLoading) {
    return <PlayerLoadingSpinner />;
  }

  if (recordings === undefined) {
    throw new Error('Recordings not found.');
  }

  if (recording === undefined) {
    throw new Error(`Recording with id "${params.id}" not found.`);
  }

  if (hasCastSession(cast)) {
    return (
      <ErrorBoundary fallback={(props) => <PlayerErrorFallback {...props} image={recording.image} />}>
        <ChromecastRecordingSenderLoader cast={cast} recording={recording} position={positionAsNumber} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary fallback={(props) => <PlayerErrorFallback {...props} image={recording.image} />}>
      <RecordingClientPlayerLoader
        recording={recording}
        recordings={recordings}
        position={castMediaPosition ?? positionAsNumber}
      />
    </ErrorBoundary>
  );
}
